import * as zod from 'zod';

import { Step } from '@sb/remote-control/types';
import { TeleopModeEnum, type TeleopMode } from '@sb/routine-runner';

export { TeleopModeEnum, type TeleopMode };

// Display labels for teleop modes (update when adding modes to TeleopModeEnum)
export const TELEOP_MODE_LABELS: Record<TeleopMode, string> = {
  arHeadset: 'AR Headset',
};

export namespace TeleopStep {
  export const name = 'Teleoperation';
  export const description =
    'Pause routine and wait for teleoperator to take control via AR headset';
  export const librarySection = Step.LibrarySection.InterfaceWithOperators;
  export const librarySort = '3';
  export const argumentKind = 'Teleop';

  export const featureFlag = 'teleopStep';

  /** Arguments to be passed to the routine runner */
  export const Arguments = zod.object({
    argumentKind: zod.literal(argumentKind),
    mode: TeleopModeEnum.default('arHeadset'),
    timeoutSeconds: zod.number().int().positive().default(300),
    tags: zod.array(zod.string().max(30)).default([]),
  });

  export type Arguments = zod.infer<typeof Arguments>;

  export const toRoutineRunner: Step.ToRoutineRunner = ({
    stepConfiguration: { args },
    stepData,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      throw new TypeError(`Expected argument kind ${argumentKind}`);
    }

    return {
      ...stepData,
      stepKind: 'Teleop',
      args: {
        mode: args.mode,
        timeoutSeconds: args.timeoutSeconds,
        tags: args.tags,
      },
    };
  };

  export const getStepDescription: Step.GetStepDescription = ({
    stepConfiguration: { args },
    includeStepName,
  }) => {
    if (args?.argumentKind !== argumentKind) {
      return null;
    }

    return [
      includeStepName ? 'Teleop' : false,
      `wait up to ${args.timeoutSeconds}s for operator`,
    ]
      .filter((line) => line !== false)
      .join(': ');
  };
}

TeleopStep satisfies Step.StepKindInfo;
